import {
  ActionIcon,
  Anchor,
  Button,
  Code,
  CopyButton,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconCopy, IconDownload } from '@tabler/icons-react';
import DesktopDownloadButton from 'src/desktop-downloads/DesktopDownloadButton';
import {
  DESKTOP_PLATFORM_LABELS,
  DESKTOP_PLATFORMS,
  DesktopInstaller,
  DesktopPlatform,
  LATEST_DESKTOP_RELEASE_URL,
  installerFileName,
  useLatestDesktopRelease,
} from 'src/desktop-downloads/latestDesktopRelease';
import PageLoader from 'src/UI/loader/PageLoader';
import { formatFileSize } from 'src/utils/fileSizeFormatting';

export default function DesktopDownloadsTab() {
  return (
    <Stack gap="xl" maw={720}>
      <Stack gap="xs" align="flex-start">
        <DesktopDownloadButton />
        <Text size="sm" c="dimmed">
          Offers the installer for the platform you are browsing on.
        </Text>
      </Stack>
      <LatestReleases />
    </Stack>
  );
}

function LatestReleases() {
  const { data: release, isLoading, isError, error } = useLatestDesktopRelease();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isError || !release) {
    return (
      <Text c="red">
        Could not load <Anchor href={LATEST_DESKTOP_RELEASE_URL}>the release manifest</Anchor>
        {error ? `: ${error.message}` : ''}
      </Text>
    );
  }

  return (
    <Stack gap="sm">
      <Text fw={600} size="lg">
        Latest release <Code>{release.version}</Code>
      </Text>
      {DESKTOP_PLATFORMS.map(platform => (
        <PlatformRelease
          key={platform}
          platform={platform}
          installer={release.installers[platform]}
        />
      ))}
    </Stack>
  );
}

function PlatformRelease({
  platform,
  installer,
}: {
  platform: DesktopPlatform;
  installer: DesktopInstaller | undefined;
}) {
  return (
    <Paper withBorder p="md">
      <Stack gap="xs">
        <Group justify="space-between">
          <Text fw={600}>{DESKTOP_PLATFORM_LABELS[platform]}</Text>
          {installer && (
            <Button
              component="a"
              href={installer.url}
              size="xs"
              variant="light"
              leftSection={<IconDownload size={14} />}
            >
              Download
            </Button>
          )}
        </Group>
        {installer ? (
          <>
            <Text size="sm">
              {installerFileName(installer)} · {formatFileSize(installer.size)}
            </Text>
            <Group gap="xs" wrap="nowrap">
              <Text size="xs" c="dimmed">
                SHA-256
              </Text>
              <Code style={{ wordBreak: 'break-all' }}>{installer.sha256}</Code>
              <CopyButton value={installer.sha256} timeout={1500}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Copied' : 'Copy checksum'} withArrow>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color={copied ? 'teal' : 'gray'}
                      onClick={copy}
                    >
                      {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>
          </>
        ) : (
          <Text size="sm" c="dimmed">
            Not included in this release.
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
